import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { buildPaths, ensureDefined } from '@e-socna/utils';
import type { BuildPathsPaths, BuildPathsParams } from '@e-socna/utils';

import { MISSION_SPACE_PARAM } from '../app/utils/mission-space.util';

import { useMissionSpace } from './useMissionSpace.hook';

type TabsMap<P extends BuildPathsPaths, T extends string> = {
  [K in keyof P]?: T;
};

export const useBuildPaths = <P extends BuildPathsPaths, T extends string>(
  paths: P,
  params: BuildPathsParams,
  tabsMap?: TabsMap<P, T>,
) => {
  const { pathname } = useLocation();
  // A mission path keeps the space of the current mission page.
  const missionSpace = useMissionSpace();

  return useMemo<{
    paths: P;
    currentPathKey: keyof P | null;
    getActiveTab: () => TabsMap<P, T>[keyof P];
  }>(() => {
    const builtPaths = buildPaths(paths, {
      [MISSION_SPACE_PARAM]: missionSpace,
      ...params,
    });

    const currentPathKey = (Object.entries(builtPaths).find(
      ([, path]) => path === pathname,
    )?.[0] ?? null) as keyof P | null;

    const getActiveTab = () =>
      ensureDefined({
        activeTab: !currentPathKey ? undefined : tabsMap?.[currentPathKey],
      }).activeTab;

    return {
      paths: builtPaths,
      currentPathKey,
      getActiveTab,
    };
  }, [missionSpace, pathname, paths, params, tabsMap]);
};
